import React, { Component } from 'react';
import { Query, Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { Alert, Button, Checkbox, Empty, Input, Modal, Select, Spin, Table, message } from 'antd';
import './ProfileChangeList.css';

const LIST = gql`
  query ProfileChanges($field: ProfileField!, $page: Int, $status: ProfileReviewStatus) {
    profileChanges(field: $field, page: $page, status: $status) {
      userId field requestId currentValue value submittedAt status reviewedAt reviewedBy
    }
  }
`;
const REVIEW = gql`
  mutation ReviewProfileChange($userId: ID!, $field: ProfileField!, $requestId: String!, $approve: Boolean!, $reason: String) {
    reviewProfileChange(userId: $userId, field: $field, requestId: $requestId, approve: $approve, reason: $reason)
  }
`;

export default class ProfileChangeList extends Component {
  state = { field: 'nickname', status: 'pending', page: 1, rejecting: null, reason: '', saving: false, selectedRowKeys: [] };

  review = async (mutate, refetch, row, approve) => {
    if (this.state.saving) return;
    const reason = this.state.reason.trim();
    if (!approve && !reason) return message.error('请填写驳回原因');
    this.setState({ saving: true });
    try {
      await mutate({ variables: { userId: row.userId, field: row.field, requestId: row.requestId, approve, reason } });
      this.setState(state => ({ rejecting: null, reason: '', selectedRowKeys: state.selectedRowKeys.filter(key => key !== row.requestId) }));
      message.success(approve ? '已通过并公开，六个月内不可再次修改' : '已驳回，原资料保持公开');
      await refetch();
    } catch (error) {
      message.error(error.message);
    } finally {
      this.setState({ saving: false });
    }
  };

  reviewBatch = async (mutate, refetch, rows, approve) => {
    if (this.state.saving || !rows.length) return;
    const reason = this.state.reason.trim();
    if (!approve && !reason) return message.error('请填写驳回原因');
    this.setState({ saving: true });
    const failed = [];
    let firstError = '';
    for (const row of rows) {
      try {
        const result = await mutate({ variables: { userId: row.userId, field: row.field, requestId: row.requestId, approve, reason } });
        if (!result.data || !result.data.reviewProfileChange) throw new Error('审核未成功');
      } catch (error) {
        failed.push(row.requestId);
        if (!firstError) firstError = error.message;
      }
    }
    this.setState({ rejecting: null, reason: '', selectedRowKeys: failed });
    if (failed.length) {
      message.error(`批量审核完成：成功 ${rows.length - failed.length} 条，失败 ${failed.length} 条。${firstError}`, 8);
    } else {
      message.success(`已批量${approve ? '通过' : '驳回'} ${rows.length} 条申请`);
    }
    try {
      await refetch();
    } catch (error) {
      message.error(`列表刷新失败，请手动刷新：${error.message}`);
    } finally {
      this.setState({ saving: false });
    }
  };

  toggleSelection = (requestId, checked) => this.setState(state => ({
    selectedRowKeys: checked
      ? Array.from(new Set([...state.selectedRowKeys, requestId]))
      : state.selectedRowKeys.filter(key => key !== requestId),
  }));

  renderValue = value => this.state.field === 'avatarUrl'
    ? (value ? <a href={value} target="_blank" rel="noopener noreferrer"><img src={value} alt="头像，点击查看大图" className="profile-review-avatar" /></a> : '未设置')
    : (value || '未设置');

  renderActions = (row, mutate, refetch) => <div className="profile-review-actions">
    <Button type="primary" disabled={this.state.saving} onClick={() => Modal.confirm({ title: '确认该资料符合社区规范并公开？', onOk: () => this.review(mutate, refetch, row, true) })}>通过</Button>
    <Button disabled={this.state.saving} onClick={() => this.setState({ rejecting: row, reason: '' })}>驳回</Button>
  </div>;

  render() {
    const { field, status, page, rejecting, reason, saving, selectedRowKeys } = this.state;
    return <div className="profile-review">
      <h2>头像昵称审核</h2>
      <Alert type="info" showIcon message="昵称与头像分别每六个月可修改一次，审核通过后公开并开始计时；驳回不占用次数。" style={{ marginBottom: 16 }} />
      <Query query={LIST} variables={{ field, page, status }} fetchPolicy="network-only" pollInterval={30000}>
        {({ loading, error, data, refetch }) => {
          const rows = (data && data.profileChanges) || [];
          const selectedRows = rows.filter(row => selectedRowKeys.includes(row.requestId));
          return <Mutation mutation={REVIEW}>
          {mutate => <div>
            {error && <Alert type="error" message={error.message} />}
            <div className="profile-review-toolbar">
              <Select aria-label="审核类型" value={`${status}:${field}`} disabled={saving} onChange={value => {
                const [nextStatus, nextField] = value.split(':');
                this.setState({ field: nextField, status: nextStatus, page: 1, rejecting: null, reason: '', selectedRowKeys: [] });
              }}>
                <Select.Option value="pending:nickname">待审核昵称</Select.Option>
                <Select.Option value="pending:avatarUrl">待审核头像</Select.Option>
                <Select.Option value="approved:nickname">已通过昵称</Select.Option>
                <Select.Option value="approved:avatarUrl">已通过头像</Select.Option>
              </Select>
              <Button disabled={loading || saving} onClick={() => refetch()}>刷新</Button>
            </div>
            {status === 'pending' && <div className="profile-review-batch">
              <Checkbox disabled={loading || saving || !rows.length} checked={!!rows.length && selectedRows.length === rows.length} indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length} onChange={event => this.setState({ selectedRowKeys: event.target.checked ? rows.map(row => row.requestId) : [] })}>全选本页</Checkbox>
              <span>已选 {selectedRows.length} 条</span>
              <Button type="primary" disabled={loading || saving || !selectedRows.length} onClick={() => Modal.confirm({
                title: `确认通过选中的 ${selectedRows.length} 条申请？`,
                content: '通过后资料将公开，六个月内不可再次修改。',
                onOk: () => this.reviewBatch(mutate, refetch, selectedRows, true),
              })}>批量通过</Button>
              <Button disabled={loading || saving || !selectedRows.length} onClick={() => this.setState({ rejecting: selectedRows, reason: '' })}>批量驳回</Button>
              <Button disabled={saving || !selectedRowKeys.length} onClick={() => this.setState({ selectedRowKeys: [] })}>取消选择</Button>
            </div>}
            <div className="profile-review-desktop">
              <Table rowSelection={status === 'pending' ? {
                selectedRowKeys: selectedRows.map(row => row.requestId),
                onChange: keys => this.setState({ selectedRowKeys: keys }),
                getCheckboxProps: () => ({ disabled: saving || loading }),
              } : undefined} rowKey="requestId" loading={loading} dataSource={(data && data.profileChanges) || []} pagination={false} scroll={{ x: status === 'approved' ? 1000 : 800 }} columns={[
                { title: '用户 ID', dataIndex: 'userId' },
                { title: '当前公开资料', dataIndex: 'currentValue', render: this.renderValue },
                { title: status === 'approved' ? '已通过资料' : '申请资料', dataIndex: 'value', render: this.renderValue },
                { title: '提交时间', dataIndex: 'submittedAt', render: value => new Date(value).toLocaleString() },
                ...(status === 'approved' ? [
                  { title: '通过时间', dataIndex: 'reviewedAt', render: value => value ? new Date(value).toLocaleString() : '—' },
                  { title: '审核人', dataIndex: 'reviewedBy', render: value => value || '—' },
                ] : [{ title: '审核', render: (_, row) => this.renderActions(row, mutate, refetch) }]),
              ]} />
            </div>
            <div className="profile-review-mobile">
              <Spin spinning={loading}>
                {!loading && !(data && data.profileChanges && data.profileChanges.length) && <Empty description={error ? '加载失败，请刷新重试' : '暂无审核记录'} />}
                {((data && data.profileChanges) || []).map(row => <article className="profile-review-card" key={row.requestId}>
                  {status === 'pending' && <Checkbox className="profile-review-card-select" checked={selectedRowKeys.includes(row.requestId)} disabled={saving || loading} onChange={event => this.toggleSelection(row.requestId, event.target.checked)}>选择此申请</Checkbox>}
                  <div className="profile-review-user"><span>用户 ID</span><strong>{row.userId}</strong></div>
                  <div className="profile-review-comparison">
                    <div><span className="profile-review-label">当前公开资料</span><div className="profile-review-value">{this.renderValue(row.currentValue)}</div></div>
                    <div><span className="profile-review-label">{status === 'approved' ? '已通过资料' : '申请资料'}</span><div className="profile-review-value">{this.renderValue(row.value)}</div></div>
                  </div>
                  <dl className="profile-review-meta">
                    <div><dt>提交时间</dt><dd>{new Date(row.submittedAt).toLocaleString()}</dd></div>
                    {status === 'approved' && <React.Fragment>
                      <div><dt>通过时间</dt><dd>{row.reviewedAt ? new Date(row.reviewedAt).toLocaleString() : '—'}</dd></div>
                      <div><dt>审核人</dt><dd>{row.reviewedBy || '—'}</dd></div>
                    </React.Fragment>}
                  </dl>
                  {status === 'pending' && this.renderActions(row, mutate, refetch)}
                </article>)}
              </Spin>
            </div>
            <div className="profile-review-pagination">
              <Button disabled={page === 1 || loading || saving} onClick={() => this.setState({ page: page - 1, selectedRowKeys: [] })}>上一页</Button>
              <span style={{ margin: 12 }}>第 {page} 页</span>
              <Button disabled={loading || saving || !data || !data.profileChanges || data.profileChanges.length < 30} onClick={() => this.setState({ page: page + 1, selectedRowKeys: [] })}>下一页</Button>
            </div>
            <Modal visible={!!rejecting} title={Array.isArray(rejecting) ? `批量驳回 ${rejecting.length} 条申请` : '驳回资料申请'} className="profile-review-modal" confirmLoading={saving} cancelButtonProps={{ disabled: saving }} closable={!saving} maskClosable={!saving} onCancel={() => this.setState({ rejecting: null })} onOk={() => Array.isArray(rejecting) ? this.reviewBatch(mutate, refetch, rejecting, false) : this.review(mutate, refetch, rejecting, false)}>
              <Input.TextArea disabled={saving} value={reason} rows={4} maxLength={200} placeholder="请填写具体原因，用户可根据原因重新提交" onChange={event => this.setState({ reason: event.target.value })} />
            </Modal>
          </div>}
        </Mutation>;
        }}
      </Query>
    </div>;
  }
}
